// Instructor lectures — short live talks the presenter delivers before turning
// learners loose on a block of courses. Each lecture is admin-only on the
// dashboard: the presenter opens it, talks through the slides, then has the
// room work the linked courses one at a time.
//
// A slide has two layers:
//   - `points` — the user-facing content. This is all a non-admin would see.
//   - `speakerNotes` — the presenter's script. Rendered ONLY for admins.
// So an admin sees both (their notes + what the room sees); a regular learner
// who lands here sees just the slide content, never the script.

export interface LectureSlide {
  /** Short label shown in the slide rail on the left. */
  shortTitle: string;
  /** Small eyebrow above the slide heading. */
  eyebrow: string;
  /** The slide heading the room sees. */
  heading: string;
  /** User-facing talking points / takeaways shown on the slide. */
  points: string[];
  /** Key into LECTURE_VISUALS for the illustration shown beside the points. */
  visual?: string;
  /** Presenter script. Admin-only. Each entry is one spoken beat. */
  speakerNotes: string[];
}

export interface Lecture {
  id: string;
  /** Eyebrow shown on the dashboard banner and lecture header. */
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Rough delivery time, shown to the presenter. */
  duration: string;
  /** Human label for the block of courses this lecture sets up. */
  coversLabel: string;
  /** Course ids this lecture introduces, in order. */
  courseIds: string[];
  slides: LectureSlide[];
}

export const LECTURES: Lecture[] = [
  {
    id: "intro-git-basics",
    eyebrow: "Instructor lecture · 5–8 min",
    title: "Why git — the mental model",
    subtitle:
      "The 'why' and the core picture before anyone touches a command. Sets up courses 1–3.",
    duration: "5–8 min",
    coversLabel: "Before courses 1–3",
    courseIds: ["foundations", "commits-branches", "remote"],
    slides: [
      {
        shortTitle: "Why we're here",
        eyebrow: "Set the scene",
        heading: "Git is how every engineering team tracks their work",
        visual: "reading",
        points: [
          "Git is version control — a tool that records the full history of a project.",
          "You don't need to write code to benefit. As a PM you'll read it daily: reviewing changes, following along on a release, understanding what shipped.",
          "Today is about the mental model first, commands second. If the picture is clear, the commands are easy.",
        ],
        speakerNotes: [
          "Open warm — ask the room: 'who has opened GitHub and felt completely lost?' Normalize it. Almost everyone has.",
          "Frame the goal: by the end of these three courses they'll be able to confidently read a pull request and follow a release — not write code.",
          "Promise them this lecture is the only 'talking at you' part for this block. After this, they drive.",
          "Keep this slide to ~60 seconds. Don't define terms yet.",
        ],
      },
      {
        shortTitle: "Snapshots",
        eyebrow: "The core idea",
        heading: "Think of git as a camera that takes snapshots",
        visual: "snapshots",
        points: [
          "Every time you save your work in git, you take a labelled snapshot of the whole project. That snapshot is called a commit.",
          "Git keeps every snapshot, forever, in order. You can always look back at — or return to — any point in time.",
          "Because nothing is ever really thrown away, git is the opposite of scary: it's a safety net.",
        ],
        speakerNotes: [
          "This is the single most important slide. If they leave with one idea, it's 'commit = snapshot.'",
          "Analogy that lands: it's like 'save game' checkpoints in a video game — you can always reload an earlier save.",
          "Emphasize the safety-net framing now; it pays off in course 6 (undoing). 'You basically can't lose work.'",
          "Don't mention staging or the working tree yet — that's course 2. Stay at the snapshot level.",
        ],
      },
      {
        shortTitle: "Branches",
        eyebrow: "Working in parallel",
        heading: "Branches let people work without stepping on each other",
        visual: "branches",
        points: [
          "A branch is just a separate line of snapshots — a safe place to try something without affecting everyone else's work.",
          "The 'official' version of the project usually lives on a branch called main.",
          "When work on a branch is ready, it gets merged back into main. That's the whole loop: branch → work → merge.",
        ],
        speakerNotes: [
          "Analogy: a branch is like editing a copy of a Google Doc, then pasting your changes back into the master doc when you're happy.",
          "Draw it in the air if you have no whiteboard: one line (main), a line splitting off (a branch), then rejoining.",
          "Tell them course 2 has a live sandbox where they'll actually make a commit and a branch in the browser — no setup.",
          "Resist going deep on merge mechanics; that's later. Keep momentum.",
        ],
      },
      {
        shortTitle: "Local vs remote",
        eyebrow: "Where the code lives",
        heading: "Your laptop and GitHub are two copies that sync",
        visual: "local-remote",
        points: [
          "Git lives on your computer (local). GitHub is a shared copy in the cloud (remote) that the whole team can reach.",
          "You push your snapshots up to GitHub to share them, and pull others' work down to stay current.",
          "GitHub isn't git — it's a place to host git projects and collaborate around them.",
        ],
        speakerNotes: [
          "Clear up the most common beginner confusion right here: git ≠ GitHub. Say it out loud.",
          "Analogy: local is the working copy on your desk; GitHub is the shared drive everyone syncs to (push = upload, pull = download).",
          "This sets up course 3 directly. Keep it light — they'll practice push/pull there.",
          "Time check: you should be around the 5-minute mark here.",
        ],
      },
      {
        shortTitle: "Now you drive",
        eyebrow: "Hand off",
        heading: "Your turn — work courses 1, 2, and 3 in order",
        visual: "track-123",
        points: [
          "Course 1 — Git, in plain English: the mental model we just covered, at your own pace.",
          "Course 2 — Commits & branches: a live sandbox where you make your first commit and branch.",
          "Course 3 — Remote vs local: push, pull, and how your laptop talks to GitHub.",
          "Take them one at a time. We'll regroup after course 3.",
        ],
        speakerNotes: [
          "Hand off clearly: tell them to open course 1 now and go in order. Set expectations on pace.",
          "Tell them you'll be around — and to use the in-app assistant or raise a hand when stuck.",
          "Decide your regroup signal now ('come back when the dashboard shows 3 of 6 complete') and say it.",
          "Then stop talking. Let them work.",
        ],
      },
    ],
  },
  {
    id: "intro-github-collab",
    eyebrow: "Instructor lecture · 6–10 min",
    title: "Collaborating on GitHub",
    subtitle:
      "Pull requests, conflicts, and the safety net — the day-to-day team workflow. Sets up courses 4–6.",
    duration: "6–10 min",
    coversLabel: "Before courses 4–6",
    courseIds: ["pull-requests", "merge-conflicts", "undoing"],
    slides: [
      {
        shortTitle: "Recap",
        eyebrow: "Pick up the thread",
        heading: "You can read history. Now let's collaborate on it.",
        visual: "recap",
        points: [
          "So far: commits are snapshots, branches are parallel work, and your laptop syncs with GitHub.",
          "These next three courses are the part you'll actually live in day-to-day on a team.",
          "It all centres on one workflow: the pull request.",
        ],
        speakerNotes: [
          "Quick recap to re-anchor — 30 seconds. Ask one person to define 'commit' in their own words to check retention.",
          "Signal the shift: block one was 'how git works'; block two is 'how teams work together with it.'",
          "Don't re-teach branches; just remind them branches exist, because PRs build on them.",
        ],
      },
      {
        shortTitle: "Pull requests",
        eyebrow: "The team workflow",
        heading: "A pull request is a proposal to merge work in",
        visual: "pull-request",
        points: [
          "Someone finishes work on a branch and opens a pull request (PR): 'here's my change, please review and merge it.'",
          "Teammates review the change, leave comments, and approve. Then it gets merged into main.",
          "The PR is where the conversation happens — it's the review step that keeps main healthy.",
        ],
        speakerNotes: [
          "This is the slide for this block. PRs are where PMs spend most of their GitHub time.",
          "Analogy: a PR is like 'suggesting edits' in a Google Doc and asking a colleague to accept them — with a discussion thread attached.",
          "Mention that course 4 shows a real diff (red/green lines) so they learn to read 'what actually changed.'",
          "Reassure them: as a PM they're often the reviewer or the reader, not the author — and reading a PR is a learnable skill.",
        ],
      },
      {
        shortTitle: "Conflicts",
        eyebrow: "When edits collide",
        heading: "Merge conflicts are normal, not a disaster",
        visual: "conflict",
        points: [
          "A conflict happens when two people change the same line and git can't decide which to keep.",
          "Git doesn't break — it pauses and asks a human to choose. That's all a conflict is: a question.",
          "There's a calm, repeatable way to resolve them. Panic is the only real risk.",
        ],
        speakerNotes: [
          "Defuse the fear up front — beginners hear 'conflict' and assume they broke something. They didn't.",
          "Analogy: two people edited the same sentence in a shared doc; git is asking 'which version do you want?'",
          "Tell them course 5 walks a conflict step-by-step in the sandbox so they've seen one before it happens for real.",
          "Keep tone light here — your calm sets theirs.",
        ],
      },
      {
        shortTitle: "Undo",
        eyebrow: "The safety net",
        heading: "You basically can't lose work in git",
        visual: "safety-net",
        points: [
          "Because git keeps every snapshot, almost any mistake is reversible.",
          "There are tools to undo a change, roll back to an earlier snapshot, or recover work you thought was gone.",
          "Knowing the undo options is what turns git from scary into safe.",
        ],
        speakerNotes: [
          "Call back to the 'safety net' idea from the first lecture — this is where it pays off.",
          "Don't drill the difference between revert/reset/reflog here; course 6 does that. Just plant 'it's recoverable.'",
          "One line that lands: 'the scariest-sounding mistakes are usually a one-line fix away.'",
        ],
      },
      {
        shortTitle: "Now you drive",
        eyebrow: "Hand off",
        heading: "Your turn — work courses 4, 5, and 6 in order",
        visual: "track-456",
        points: [
          "Course 4 — Pull requests on GitHub: open, review, approve, and read a real diff.",
          "Course 5 — Merge conflicts, calmly: a step-by-step you can rely on.",
          "Course 6 — Undoing & recovering: revert, reset, and why work is hard to lose.",
          "One at a time. Finishing course 6 completes the track.",
        ],
        speakerNotes: [
          "Final hand-off. Same drill as last time: open course 4, go in order, ask when stuck.",
          "Set the finish line: completing course 6 means the full track is done — call that out as the goal for the session.",
          "Offer a short wrap-up at the end where you take questions once everyone's through.",
          "Then let them work.",
        ],
      },
    ],
  },
];

export function lectureById(id: string): Lecture | undefined {
  return LECTURES.find((l) => l.id === id);
}
